// src/components/cart/CartButton.tsx
import { useState, useRef, useEffect } from "react";
import { ShoppingCart, Mail } from "lucide-react";
import { cartService } from "../../services/cartService";
import { useUser } from "@clerk/clerk-react";

/**
 * Simplified cart button that opens the FastSpring cart when clicked
 */
const CartButton: React.FC = () => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const { isSignedIn, user } = useUser();
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the modal to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setShowEmailModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Pre-fill email from Clerk if user is signed in
  useEffect(() => {
    if (isSignedIn && user?.primaryEmailAddress?.emailAddress) {
      const userEmail = user.primaryEmailAddress.emailAddress;
      setEmail(userEmail);

      // Also save it to storage for FastSpring
      cartService.saveCustomerEmail(userEmail);
    } else {
      // Try to load from localStorage
      const savedEmail = cartService.getCustomerEmail();
      if (savedEmail) {
        setEmail(savedEmail);
      }
    }
  }, [isSignedIn, user]);

  const handleCartOpen = () => {
    // If we have an email, proceed to cart view
    const savedEmail = cartService.getCustomerEmail();
    if (savedEmail) {
      cartService.viewCart();
    } else {
      // Otherwise, prompt for email
      setShowEmailModal(true);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (email && email.includes("@")) {
      cartService.saveCustomerEmail(email);
      setShowEmailModal(false);
      cartService.viewCart();
    }
  };

  return (
    <>
      <div className="flex items-center">
        {/* Cart Icon Button */}
        <button
          onClick={handleCartOpen}
          className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Shopping cart"
        >
          <ShoppingCart className="h-5 w-5" />
        </button>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            ref={modalRef}
            className="bg-card rounded-lg shadow-lg p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-lg font-semibold mb-4">
              Please enter your email
            </h3>
            <p className="text-muted-foreground mb-4">
              We'll use this to save your cart and send you order confirmation.
            </p>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1"
                >
                  Email Address
                </label>
                <div className="flex">
                  <div className="bg-muted flex items-center px-3 rounded-l-md border-y border-l">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-r-md focus:outline-none focus:ring-2 focus:ring-primary dark:text-gray-800"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 border rounded-md hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md"
                >
                  Continue to Cart
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CartButton;
